/**
 * Textos para mostrar (es-CL) de los códigos que entrega la API. Un código
 * desconocido nunca rompe la UI: cae a una versión legible del propio código.
 */

import type { IdentifierView, LiveHit, LiveStatus, MatchField, ParsedQuery } from "../../shared/contracts.js";
import { titleCase } from "./format.js";

type HitCategory = LiveHit["category"];

const SOURCE_LABELS: Record<string, string> = {
  yapo: "Yapo",
  mercadolibre: "MercadoLibre",
  "mercadolibre-web": "MercadoLibre",
  "facebook-marketplace": "Facebook Marketplace",
  "facebook-grupo": "Facebook (grupo)",
  "facebook-grupo-noticias": "Facebook (grupo de noticias)",
  "facebook-grupo-comercial": "Facebook (grupo uso comercial)",
  facebook: "Facebook",
  "instagram-perfil": "Instagram",
  instagram: "Instagram",
  patentechile: "PatenteChile",
  autoseguro: "AutoSeguro",
  chileautos: "Chileautos",
  tattersall: "Tattersall",
  macal: "Macal",
  vedisa: "Vedisa",
  manquehue: "Remates Manquehue",
  karcal: "Karcal",
  chilebid: "Chilebid",
  noticias: "Noticias",
  "zarate-siniestrados": "Zárate (siniestrados)",
  vendoautos: "VendoAutos",
  checkeados: "Checkeados",
  autosummit: "AutoSummit",
  pompeyo: "Pompeyo",
};

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? titleCase(source.replace(/[-_]+/g, " "));
}

const IDENT_SOURCE: Record<IdentifierView["source"], string> = {
  api: "dato estructurado del aviso",
  slug: "en la URL del aviso",
  title: "en el título del aviso",
  text: "en el texto del aviso",
  ocr_local: "leída por OCR desde las fotos",
  ocr_gemini: "leída por OCR (Gemini) desde las fotos",
  ocr_vision: "leída por OCR (visión) desde las fotos",
  ocr_foto: "vista en una foto del aviso, en OTRO vehículo (al lado o al fondo) o en un letrero: no es la patente del auto publicado",
  registry: "consulta de registro de patentes",
  manual: "ingresada manualmente",
  legacy: "dato heredado de la versión anterior",
};

export function identifierProvenance(id: Pick<IdentifierView, "source" | "strength" | "confidence">): string {
  const where = IDENT_SOURCE[id.source] ?? id.source;
  const strength = id.strength === "strong" ? "Dato confiable" : "Dato débil (puede ser un error de lectura)";
  const conf = id.confidence !== null ? ` · confianza ${Math.round(id.confidence * 100)} %` : "";
  return `${strength}: ${where}${conf}`;
}

export function identifierSourceLabel(source: IdentifierView["source"]): string {
  return IDENT_SOURCE[source] ?? source;
}

export const IDENTIFIER_KIND_LABELS: Record<IdentifierView["kind"], string> = {
  plate: "Patente",
  vin: "VIN / chasis",
  rut: "RUT",
  phone: "Teléfono",
  motor: "N° de motor",
};

export const MATCH_FIELD_LABELS: Record<MatchField, string> = {
  plate: "Patente",
  plate_near: "Patente parecida",
  vin: "VIN",
  rut: "RUT",
  phone: "Teléfono",
  motor: "N° motor",
  name: "Nombre",
  make: "Marca",
  model: "Modelo",
  year: "Año",
  color: "Color",
  location: "Ubicación",
  email: "Correo",
  incident: "Fecha del siniestro",
  text: "Texto",
};

export const SCORE_PART_LABELS: Record<string, string> = {
  plate: "patente",
  name: "nombre",
  fts: "texto",
  text: "texto",
  veh: "vehículo",
  rec: "recencia",
  id: "identificador",
};

export const INTENT_LABELS: Record<ParsedQuery["intent"], string> = {
  empty: "Vacía",
  plate: "Patente",
  plate_prefix: "Inicio de patente",
  vin: "VIN / chasis",
  rut: "RUT",
  phone: "Teléfono",
  person: "Persona",
  vehicle: "Vehículo",
  email: "Correo",
  company: "Empresa",
  mixed: "Mixta",
  freetext: "Texto libre",
};

export const PLATE_STRENGTH_LABELS: Record<ParsedQuery["plates"][number]["strength"], string> = {
  exact: "escrita tal cual",
  joined: "armada desde varias palabras",
  corrected: "corregida (letra↔número)",
  weak: "posible patente: también se busca como texto",
};

/** Avisos del parser → sugerencias en lenguaje simple. */
export function warningText(w: ParsedQuery["warnings"][number]): string {
  const [code, arg = ""] = w.split(/:(.*)/s);
  switch (code) {
    case "empty_query":
      return "La búsqueda está vacía.";
    case "name_too_broad":
      return "Un nombre de una sola palabra es muy amplio: agrega el apellido, la marca, el modelo o el año para acotar.";
    case "name_may_be_model":
      return "Una palabra del nombre también es un modelo de vehículo. Si buscas el vehículo, agrega la marca.";
    case "year_out_of_range":
      return "El año indicado está fuera de rango y no se usó como filtro.";
    case "plate_or_other":
      return `«${arg.toUpperCase()}» puede ser una patente u otro dato (año, modelo o versión): se buscó de ambas formas.`;
    case "plate_invalid":
      return `«${arg.toUpperCase()}» no tiene formato de patente chilena (desde 2007 no llevan vocales ni M, N, Q; antes, 2 letras y 4 dígitos). Revísala: se buscó como inicio de patente.`;
    case "ambiguous_model":
      return `«${titleCase(arg)}» puede ser un modelo o una palabra común/apellido: solo filtra si agregas la marca o el año.`;
    case "ambiguous_location":
      return `«${titleCase(arg)}» puede ser una comuna o un apellido: se usa solo para ordenar, no para filtrar.`;
    default:
      return w;
  }
}

export const LIVE_STATUS_LABELS: Record<LiveStatus, string> = {
  partial: "Resultados incompletos",
  queued: "En cola",
  running: "Buscando…",
  done: "Completada",
  error: "Con errores",
  disabled: "Desactivada",
};

export const CATEGORY_LABELS: Record<HitCategory, string> = {
  news: "Noticia",
  social: "Red social",
  classifieds: "Clasificado",
  gov: "Gobierno",
  judicial: "Judicial",
  company: "Empresa",
  other: "Otro",
};

export function categoryLabel(c: string): string {
  return (CATEGORY_LABELS as Record<string, string>)[c] ?? titleCase(c);
}

export const LIVE_ENTITY_LABELS: Record<"person" | "plate" | "vehicle" | "email" | "company" | "text" | "vin" | "rut" | "phone" | "phrase" | "within", string> = {
  rut: "RUT",
  phrase: "Mismo aviso en otros sitios",
  within: "Dentro de la cuenta o grupo",
  phone: "Teléfono",
  vin: "VIN",
  person: "Persona",
  plate: "Patente",
  vehicle: "Vehículo",
  email: "Correo",
  company: "Empresa",
  text: "Texto",
};

const PROVIDER_LABELS: Record<string, string> = {
  serpapi: "Google (SerpApi)",
  serper: "Google (Serper)",
  brave: "Brave Search",
  mock: "Simulado",
};

export function providerLabel(p: string): string {
  return PROVIDER_LABELS[p] ?? p;
}

const AUDIT_ACTIONS: Record<string, string> = {
  "auth.login.ok": "Inicio de sesión",
  "auth.login.fail": "Inicio de sesión fallido",
  "auth.login.blocked": "Inicio de sesión bloqueado",
  "auth.logout": "Cierre de sesión",
  "auth.password.change": "Cambio de contraseña",
  "auth.token.redeem": "Contraseña definida con enlace",
  "auth.session.revoke": "Sesión cerrada por el usuario",
  "search.run": "Búsqueda",
  "search.page": "Más resultados de búsqueda",
  "search.view": "Búsqueda reabierta",
  "listing.view": "Vista de aviso",
  "snapshot.raw": "Descarga de JSON crudo",
  "snapshot.preview": "Vista de HTML del aviso",
  "snapshot.detail": "Vista de página de detalle",
  "snapshot.screenshot": "Vista de captura",
  "search.summary": "Resumen con IA",
  "web.lookup": "Consulta en internet",
  "live.run": "Búsqueda en vivo",
  "admin.institution.create": "Institución creada",
  "admin.institution.update": "Institución modificada",
  "admin.institution.suspend": "Institución suspendida",
  "admin.institution.reactivate": "Institución reactivada",
  "admin.user.create": "Usuario creado",
  "admin.user.update": "Usuario modificado",
  "admin.user.disable": "Usuario deshabilitado",
  "admin.user.enable": "Usuario habilitado",
  "admin.user.password_reset": "Restablecimiento de contraseña",
  "admin.user.unlock": "Usuario desbloqueado",
  "admin.user.sessions_revoke": "Sesiones del usuario cerradas",
  "admin.audit.export": "Exportación de auditoría",
  deny: "Acceso denegado",
};

export function auditActionLabel(action: string): string {
  return AUDIT_ACTIONS[action] ?? action;
}

/** Acciones conocidas, para sugerir en el filtro de auditoría. */
export const KNOWN_AUDIT_ACTIONS = Object.keys(AUDIT_ACTIONS);

export const ROLE_LABELS: Record<"user" | "admin", string> = {
  user: "Usuario",
  admin: "Administrador",
};

export const USER_STATUS_LABELS: Record<"invited" | "active" | "disabled", string> = {
  invited: "Invitado",
  active: "Activo",
  disabled: "Deshabilitado",
};

export const INSTITUTION_STATUS_LABELS: Record<"active" | "suspended", string> = {
  active: "Activa",
  suspended: "Suspendida",
};

/** "Mozilla/5.0 (Windows NT 10.0…) Chrome/141" → "Chrome en Windows". */
export function describeUserAgent(ua: string | null): string {
  if (!ua) return "Navegador desconocido";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari"
    : "Navegador";
  const os = /Windows/.test(ua) ? "Windows"
    : /Android/.test(ua) ? "Android"
    : /iPhone|iPad|iOS/.test(ua) ? "iOS"
    : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Linux/.test(ua) ? "Linux"
    : "sistema desconocido";
  return `${browser} en ${os}`;
}
